import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionEyebrow from "@/components/SectionEyebrow";
import Seo from "@/components/Seo";
import FAQ from "@/components/FAQ";
import { useLang } from "@/context/LanguageContext";
import { caseStudies } from "@/data/caseStudies";
import { caseStudiesFaqs } from "@/data/faqs";
import { faqJsonLd } from "@/lib/seo";

export default function CaseStudies() {
    const { t } = useLang();
    return (
        <div data-testid="case-studies-page" className="relative">
            <Seo
                title="Case Studies · Real Brand & Business Growth Stories — Abhijit JK"
                description="Real brand growth stories — branding, packaging, websites, social media and UI/UX — and how small businesses became visible brands. By Abhijit JK, Founder of Ragi Media, Bengaluru."
                keywords="brand identity design, packaging design, website design case study, UI UX design, social media growth, business growth case studies, Abhijit JK, Ragi Media"
                path="/case-studies"
                jsonLd={faqJsonLd(caseStudiesFaqs)}
            />

            {/* HERO */}
            <section className="mx-auto max-w-7xl px-6 pt-20 pb-12 lg:px-10">
                <Reveal>
                    <SectionEyebrow>{t("cases_eyebrow")}</SectionEyebrow>
                </Reveal>
                <Reveal delay={0.1}>
                    <h1
                        data-testid="case-studies-headline"
                        className="mt-6 max-w-4xl font-display text-4xl font-semibold leading-[1.04] tracking-tighter sm:text-5xl lg:text-7xl"
                    >
                        {t("cases_pre")} <span className="gold-text">{t("cases_gold")}</span>
                    </h1>
                </Reveal>
                <Reveal delay={0.15}>
                    <p className="mt-6 max-w-3xl text-base text-white/65 md:text-lg">
                        {t("cases_sub")}
                    </p>
                </Reveal>
            </section>

            {/* CASE STUDIES — image first, alternating */}
            <section className="mx-auto max-w-7xl px-6 pb-12 lg:px-10">
                <div className="space-y-20 lg:space-y-28">
                    {caseStudies.map((cs, i) => {
                        const Icon = cs.icon;
                        const flip = i % 2 === 1;
                        return (
                            <Reveal key={cs.id}>
                                <div
                                    data-testid={`case-study-card-${cs.id}`}
                                    className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16"
                                >
                                    <div className={flip ? "lg:order-2" : ""}>
                                        <div className="glass glow-border overflow-hidden rounded-lg">
                                            <img
                                                src={cs.image}
                                                alt={`${cs.title} — ${cs.category} case study by Abhijit JK, Ragi Media`}
                                                loading="lazy"
                                                className="h-full w-full object-cover"
                                            />
                                        </div>
                                    </div>
                                    <div className={flip ? "lg:order-1" : ""}>
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-11 w-11 items-center justify-center rounded-sm bg-gold-300/10 text-gold-300">
                                                <Icon size={18} />
                                            </div>
                                            <span className="label-eyebrow">{cs.category}</span>
                                        </div>
                                        <h2 className="mt-5 font-display text-2xl font-medium leading-tight sm:text-3xl lg:text-4xl">
                                            {cs.title}
                                        </h2>
                                        <p className="mt-5 text-sm leading-relaxed text-white/70 md:text-base">
                                            {cs.problem}
                                        </p>
                                        <p className="mt-4 text-sm leading-relaxed text-white/70 md:text-base">
                                            {cs.solution}
                                        </p>
                                        <div className="mt-8 grid grid-cols-3 gap-4">
                                            {cs.metrics.map((m) => (
                                                <div key={m.label} className="glass rounded-md p-4 text-center">
                                                    <p className="font-display text-xl font-semibold gold-text sm:text-2xl">
                                                        {m.value}
                                                    </p>
                                                    <p className="mt-1 text-[10px] uppercase tracking-[0.16em] text-white/55">
                                                        {m.label}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
                <Reveal>
                    <div className="glass glow-border relative overflow-hidden rounded-lg p-10 text-center md:p-16">
                        <span className="pointer-events-none absolute -top-32 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full opacity-40 blur-3xl" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.4), transparent 60%)" }} />
                        <div className="relative">
                            <h2 className="mx-auto max-w-3xl font-display text-3xl font-medium leading-tight sm:text-4xl lg:text-5xl">
                                Your business can be the{" "}
                                <span className="gold-text">next success story.</span>
                            </h2>
                            <Link to="/booking" data-testid="case-studies-book-cta" className="gold-button mt-10 inline-flex items-center gap-2">
                                Book a Strategy Call <ArrowRight size={14} />
                            </Link>
                        </div>
                    </div>
                </Reveal>
            </section>

            <FAQ items={caseStudiesFaqs} eyebrow="FAQ" title="Results &" gold="what to expect." />
        </div>
    );
}
